import React from 'react'

import { observer } from 'mobx-react-lite'

import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { ChevronRight } from '@mxenabled/mx-icons'
import { H3, Text } from '@mxenabled/mxui'

import {
  ConnectDrawer,
  Loader,
  useAccountStore,
  useBudgetsStore,
  useCategoryStore,
  useFinstrongStore,
  useGlobalCopyStore,
  useGlobalUiStore,
  useRecurringTransactionsStore,
  useScreenSize,
  useUserStore,
  useWidgetLoadTimer,
  WidgetContainer,
} from '../common'
import { type WidgetProps } from '../core'
import { LineChart } from '../common/components/charts/LineChart'

import { ConnectMoreAccountsCard } from './components/ConnectMoreAccountsCard'
import KeyIndicatorsTabsWidget from './components/KeyIndicatorsTabsWidget'
import MissingData from './components/MissingData'
import Onboarding from './components/onboarding/Onboarding'
import ProfileDrawer from './components/ProfileDrawer'
import ScoreHistoryDrawer from './components/ScoreHistoryDrawer'
import { FinancialStrengthCard } from './FinancialStrengthCard'

const FinstrongWidget = ({ onBackClick, onMenuClick, sx, onPrimaryCtaClick }: WidgetProps) => {
  const { visibleAccounts: accounts } = useAccountStore()
  const { dateRange, isDataLoaded, loadBudgetData, setTheme } = useBudgetsStore()
  const { reloadCategoryTotals } = useCategoryStore()
  const finstrongStore = useFinstrongStore()
  const {
    augmentedChartData,
    calculateHealthScore,
    loadAugmentedChartData,
    loadMonthlySummaries,
    loadPeerScore,
    peerScore,
    healthScore,
  } = finstrongStore
  const { finstrong: finstrongCopy, connect: connectCopy } = useGlobalCopyStore()
  const { isInitialized } = useGlobalUiStore()
  const { loadRepeatingTransactionData, isDataLoaded: isRepeatingDataLoaded } =
    useRecurringTransactionsStore()
  const { isSmallMobile } = useScreenSize()
  const theme = useTheme()
  const { userProfile } = useUserStore()

  const [isFinstrongLoaded, setIsFinstrongLoaded] = React.useState(false)
  const [isScoreChangeLoading, setIsScoreChangeLoading] = React.useState(false)
  const [isProfileOpen, setIsProfileOpen] = React.useState(false)
  const [isScoreHistoryOpen, setIsScoreHistoryOpen] = React.useState(false)
  const [showConnectWidget, setShowConnectWidget] = React.useState(false)

  useWidgetLoadTimer({
    widgetName: 'FinstrongWidget',
    isLoaded: isFinstrongLoaded,
  })

  React.useEffect(() => {
    setTheme(theme)
  }, [])

  React.useEffect(() => {
    if (onPrimaryCtaClick) {
      finstrongStore.handleFinstrongWidgetPrimaryCtaClick = onPrimaryCtaClick
    }
  }, [onPrimaryCtaClick, finstrongStore])

  React.useEffect(() => {
    if (isInitialized) {
      loadMonthlySummaries().finally()
      loadPeerScore().finally()
      calculateHealthScore().finally()
    }
  }, [isInitialized])

  // `accounts` (accountStore.visibleAccounts) is a MobX computed that returns a
  // NEW filtered array on every access. Depending on the array reference here
  // caused an infinite loop: reloadCategoryTotals -> observed category state
  // changes -> observer re-renders -> visibleAccounts recomputes to a new array
  // -> this effect re-fires -> reloadCategoryTotals again -> ... (unbounded, it
  // exhausted the jsdom worker's V8 heap in tests and needlessly re-fetched in
  // the app). Depend on a stable primitive signature of the account guids so
  // the effect only re-runs when the actual set of accounts changes.
  const accountsKey = accounts.map((account) => account.guid).join(',')

  React.useEffect(() => {
    if (!isInitialized) return

    reloadCategoryTotals(accounts, dateRange.start, dateRange.end).finally(() => {
      setIsFinstrongLoaded(true)
    })
    if (!isDataLoaded) {
      loadBudgetData()
      loadRepeatingTransactionData().finally()
    }
  }, [dateRange, isDataLoaded, isInitialized, accountsKey])

  React.useEffect(() => {
    if (healthScore?.health_score) {
      loadAugmentedChartData().finally()
    }
  }, [healthScore.health_score])

  const handleProfileClick = () => setIsProfileOpen(true)
  const average = Math.round(peerScore?.average_health_score?.average_health_score || 0)
  const hasValidData = augmentedChartData.some((item) => item.y !== 0)

  const handleCloseProfileDrawer = () => {
    setIsScoreChangeLoading(true)
    calculateHealthScore(false).finally(() => {
      setIsScoreChangeLoading(false)
    })
    setIsProfileOpen(false)
  }

  if (!isInitialized || !isFinstrongLoaded || !isRepeatingDataLoaded || isScoreChangeLoading)
    return <Loader />

  return (
    <WidgetContainer
      actions={[
        {
          iconName: 'person',
          label: finstrongCopy.profile,
          onClick: handleProfileClick,
        },
      ]}
      hasDivider={true}
      onBackClick={onBackClick}
      onMenuClick={onMenuClick}
      showAccountFilter={false}
      sx={sx}
      title={finstrongCopy.title}
    >
      {userProfile.has_completed_finstrong_onboarding ? (
        <Stack
          sx={{
            bgcolor: 'background.default',
            flexDirection: { md: 'row' },
            gap: 24,
            justifyContent: 'center',
            pt: isSmallMobile ? 16 : 24,
            px: isSmallMobile ? 16 : 48,
          }}
        >
          <Stack sx={{ gap: 24, width: { md: 432 }, height: '100%' }}>
            <FinancialStrengthCard />
            <ConnectMoreAccountsCard
              description={finstrongCopy.connect_more_accounts_description}
              drawerType="connectDrawer"
              title={finstrongCopy.connect_more_accounts_title}
            />
          </Stack>
          <Stack sx={{ maxWidth: 848, width: { md: 720 } }}>
            <Card sx={{ height: '100%', p: 16 }}>
              <Stack
                direction="row"
                sx={{ alignItems: 'center', gap: 12, justifyContent: 'space-between' }}
              >
                <H3>{finstrongCopy.key_indicators}</H3>

                <Button
                  endIcon={<ChevronRight />}
                  onClick={() => setIsScoreHistoryOpen(true)}
                  size="small"
                  sx={{
                    '&.MuiButtonBase-root': {
                      px: 8,
                      py: 4,
                    },
                  }}
                  variant="text"
                >
                  <Text bold={true} variant="body2">
                    {finstrongCopy.whats_changed}
                  </Text>
                </Button>
              </Stack>
              {hasValidData ? (
                <LineChart
                  axisColor={theme.palette.divider}
                  baseline="min"
                  colors={[theme.palette.primary.main]}
                  curveType="natural"
                  customLegendBoxSx={{ borderRadius: '2px' }}
                  customLegendSx={{ left: 0 }}
                  customLegendVariant="caption"
                  datasets={[augmentedChartData]}
                  height={300}
                  isCurrency={false}
                  isGraphClippingXAxis={true}
                  labelBackgroundColor={theme.palette.neutral.dark}
                  labelFontColor={theme.palette.common.white}
                  labelFontSize={
                    typeof theme.typography.caption?.fontSize === 'string'
                      ? parseFloat(theme.typography.caption.fontSize)
                      : theme.typography.caption?.fontSize
                  }
                  labels={[finstrongCopy.key_indicators_label]}
                  margin={{ right: 12, left: -37 }}
                  peerAverage={average}
                  showArea={true}
                  showAverage={true}
                  showLegend={true}
                  showMarkLabel={true}
                  showTooltip={true}
                  showXAxis={true}
                  title={finstrongCopy.peer_average}
                />
              ) : (
                <MissingData onClick={() => setShowConnectWidget(true)} />
              )}
              <KeyIndicatorsTabsWidget />
            </Card>
          </Stack>
        </Stack>
      ) : (
        <Onboarding />
      )}

      <ProfileDrawer isOpen={isProfileOpen} onClose={handleCloseProfileDrawer} />
      <ConnectDrawer
        onClose={() => setShowConnectWidget(false)}
        showConnectWidget={showConnectWidget}
        title={connectCopy.mini_title}
      />
      <ScoreHistoryDrawer
        isOpen={isScoreHistoryOpen}
        onClose={() => setIsScoreHistoryOpen(false)}
      />
    </WidgetContainer>
  )
}

export default observer(FinstrongWidget)
